import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  CreditCard, 
  DollarSign, 
  CheckCircle2, 
  Clock, 
  XCircle,
  Receipt,
  FileText,
  Building2
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

export default function PaymentsPage() {
  const { user } = useAuth();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPaymentHistory();
    }
  }, [user]);

  const fetchPaymentHistory = async () => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${backendUrl}/api/payments/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      // Don't show error toast if it's just empty history
      if (error.response?.status !== 404) {
        toast.error('Failed to load payment history');
      }
      setPaymentHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'pending':
      case 'initiated':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'failed':
      case 'expired':
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || 'pending';
    const config = {
      paid: { variant: 'default', className: 'bg-green-100 text-green-700 hover:bg-green-100' },
      completed: { variant: 'default', className: 'bg-green-100 text-green-700 hover:bg-green-100' },
      success: { variant: 'default', className: 'bg-green-100 text-green-700 hover:bg-green-100' },
      pending: { variant: 'secondary', className: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100' },
      initiated: { variant: 'secondary', className: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100' },
      failed: { variant: 'destructive', className: 'bg-red-100 text-red-700 hover:bg-red-100' },
      expired: { variant: 'destructive', className: 'bg-red-100 text-red-700 hover:bg-red-100' },
      cancelled: { variant: 'destructive', className: 'bg-red-100 text-red-700 hover:bg-red-100' },
    };
    
    const { className } = config[statusLower] || { className: 'bg-gray-100 text-gray-700' };
    
    return (
      <Badge className={className}>
        {statusLower.charAt(0).toUpperCase() + statusLower.slice(1)}
      </Badge>
    );
  };

  const getEntityIcon = (entityType) => {
    if (entityType?.toLowerCase().includes('llc')) {
      return <Building2 className="h-4 w-4 text-muted-foreground" />;
    }
    return <FileText className="h-4 w-4 text-muted-foreground" />;
  };

  const formatEntityType = (entityType) => {
    if (!entityType) return 'Payment';
    if (entityType.toLowerCase().includes('llc')) return 'LLC Formation';
    if (entityType.toLowerCase().includes('tax')) return 'Tax Filing';
    return entityType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Calculate summary stats
  const totalPaid = paymentHistory
    .filter(p => ['paid', 'completed', 'success'].includes(p.status?.toLowerCase()))
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  
  const successCount = paymentHistory.filter(p => 
    ['paid', 'completed', 'success'].includes(p.status?.toLowerCase())
  ).length;
  
  const failedCount = paymentHistory.filter(p => 
    ['failed', 'expired', 'cancelled'].includes(p.status?.toLowerCase())
  ).length;

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <DashboardLayout role="client" title="Payment History" user={user}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Payment History</h2>
          <p className="mt-1 text-muted-foreground">
            View your payment transactions and history
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">${totalPaid.toFixed(2)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Successful Payments</p>
                  <p className="text-2xl font-bold text-green-600">{successCount}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Failed Payments</p>
                  <p className="text-2xl font-bold text-red-600">{failedCount}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment History */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Transaction History</CardTitle>
                <CardDescription>All your payment transactions</CardDescription>
              </div>
              <Receipt className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Clock className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="mt-2 text-muted-foreground">Loading payment history...</p>
              </div>
            ) : paymentHistory.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                <p className="mt-4 text-lg font-medium text-muted-foreground">No payment history yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your payment transactions will appear here once you make a payment
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {paymentHistory.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {getStatusIcon(payment.status)}
                      <div>
                        <div className="flex items-center gap-2">
                          {getEntityIcon(payment.entity_type)}
                          <p className="font-medium">{formatEntityType(payment.entity_type)}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {payment.created_at ? new Date(payment.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'N/A'}
                        </p>
                        {payment.description && (
                          <p className="text-xs text-muted-foreground mt-1">{payment.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-lg">
                        ${(payment.amount || 0).toFixed(2)} <span className="text-xs text-muted-foreground">{(payment.currency || 'USD').toUpperCase()}</span>
                      </p>
                      <div className="mt-1">
                        {getStatusBadge(payment.status)}
                      </div>
                      {payment.paid_at && ['paid', 'completed', 'success'].includes(payment.status?.toLowerCase()) && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Paid: {new Date(payment.paid_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
